/**
 * レース情報カード - Boss1要求仕様準拠
 * 日付・競馬場・レース番号・発走時刻を明確に表示
 */

import React from 'react';
import { Calendar, Clock, MapPin, Trophy, Users, TrendingUp } from 'lucide-react';

export function RaceInformationCard({ race, prediction }) {
  // 日付フォーマット（例: 2025年6月21日(土)）
  const formatDate = (dateStr) => {
    const date = new Date(dateStr || Date.now());
    const days = ['日', '月', '火', '水', '木', '金', '土'];
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const dayOfWeek = days[date.getDay()];
    return `${year}年${month}月${day}日(${dayOfWeek})`;
  };

  // 時刻フォーマット（例: 10:00発走）
  const formatTime = (timeStr) => {
    if (!timeStr) return '未定';
    return `${timeStr}発走`;
  };

  const cardStyle = {
    backgroundColor: 'white',
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    border: '1px solid #e5e7eb',
    padding: '24px',
    marginBottom: '24px',
    transition: 'all 0.3s ease',
    ':hover': {
      boxShadow: '0 6px 30px rgba(0, 0, 0, 0.12)',
      transform: 'translateY(-2px)'
    }
  };

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
    paddingBottom: '20px',
    borderBottom: '2px solid #f3f4f6'
  };

  const titleStyle = {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1f2937',
    marginBottom: '8px'
  };

  const infoGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '24px'
  };

  const infoItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    border: '1px solid #e5e7eb'
  };

  const iconStyle = {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#3b82f6',
    color: 'white'
  };

  const labelStyle = {
    fontSize: '12px',
    color: '#6b7280',
    marginBottom: '2px'
  };

  const valueStyle = {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1f2937'
  };

  const statusBadgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '500',
    backgroundColor: prediction ? '#10b981' : '#6b7280',
    color: 'white'
  };

  return (
    <div style={cardStyle} data-testid="race-information-card">
      {/* ヘッダー部分 */}
      <div style={headerStyle}>
        <div>
          <h2 style={titleStyle}>
            {race.track || '東京'} {race.race_number || 1}R - {race.race_name || 'レース名未定'}
          </h2>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <span style={{ color: '#6b7280', fontSize: '14px' }}>
              {race.grade && `【${race.grade}】`}
            </span>
            <span style={{ color: '#6b7280', fontSize: '14px' }}>
              {race.distance || '1600'}m {race.track_type || '芝'}
            </span>
          </div>
        </div>
        <div style={statusBadgeStyle}>
          {prediction ? (
            <>
              <Trophy size={16} />
              予測完了
            </>
          ) : (
            <>
              <Clock size={16} />
              予測待機中
            </>
          )}
        </div>
      </div>

      {/* レース情報グリッド */}
      <div style={infoGridStyle}>
        {/* 日付 */}
        <div style={infoItemStyle}>
          <div style={{ ...iconStyle, backgroundColor: '#8b5cf6' }}>
            <Calendar size={20} />
          </div>
          <div>
            <div style={labelStyle}>開催日</div>
            <div style={valueStyle}>{formatDate(race.date)}</div>
          </div>
        </div>

        {/* 発走時刻 */}
        <div style={infoItemStyle}>
          <div style={{ ...iconStyle, backgroundColor: '#10b981' }}>
            <Clock size={20} />
          </div>
          <div>
            <div style={labelStyle}>発走時刻</div>
            <div style={valueStyle}>{formatTime(race.post_time)}</div>
          </div>
        </div>

        {/* 競馬場 */}
        <div style={infoItemStyle}>
          <div style={{ ...iconStyle, backgroundColor: '#f59e0b' }}>
            <MapPin size={20} />
          </div>
          <div>
            <div style={labelStyle}>競馬場</div>
            <div style={valueStyle}>{race.track || '東京'}競馬場</div>
          </div>
        </div>

        {/* 出走頭数 */}
        <div style={infoItemStyle}>
          <div style={{ ...iconStyle, backgroundColor: '#ef4444' }}>
            <Users size={20} />
          </div>
          <div>
            <div style={labelStyle}>出走頭数</div>
            <div style={valueStyle}>{race.horse_count || 18}頭</div>
          </div>
        </div>
      </div>

      {/* 予測結果サマリー（予測がある場合のみ） */}
      {prediction && (
        <div style={{
          padding: '16px',
          backgroundColor: '#f0fdf4',
          borderRadius: '12px',
          border: '1px solid #86efac'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <TrendingUp size={20} color="#10b981" />
            <span style={{ fontSize: '16px', fontWeight: '600', color: '#059669' }}>
              AI予測結果
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
            <div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>推奨馬</div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                {prediction.top_horse || '1番 エフフォーリア'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>予想オッズ</div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                {prediction.expected_odds || '3.2'}倍
              </div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>信頼度</div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                {prediction.confidence || '95'}%
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}